import {GitHubAvatar} from '@github-ui/github-avatar'
import {IssueStatusIcon} from '@github-ui/issue-pr-state/IssueStatusIcon'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {ColorDecorator} from '@github-ui/shared-single-select-components/ColorDecorator'
import type {ColorName} from '@github-ui/use-named-color'
import type {ReactNode} from 'react'

import styles from './axis-heading.module.css'
import type {AxisColor, BoardPayloadAxisLabel} from './board-payload'
import {toIssueStatusIconStatus} from './issue-status'
import {RepositoryIcon} from './RepositoryIcon'

/** The wire's role colour names in the casing `@github-ui/use-named-color` reads. */
const NAMED_COLORS: Record<AxisColor, ColorName> = {
  gray: 'GRAY',
  blue: 'BLUE',
  green: 'GREEN',
  yellow: 'YELLOW',
  orange: 'ORANGE',
  red: 'RED',
  pink: 'PINK',
  purple: 'PURPLE',
}

type AxisLeadingVisual = {
  leadingVisual: ReactNode
  leadingVisualAriaHidden?: boolean
}

/**
 * The mark the grouping field gives a bucket, or nothing where its field gives it none.
 *
 * A role colour is drawn through `ColorDecorator`, which resolves it to a Primer token so it
 * follows the reader's theme. A literal one is a swatch of exactly that value, because a
 * label's colour is the account's own and has no token to resolve to.
 *
 * It comes from the payload's axis rather than from the cards in the bucket, so an option
 * nothing has been given is drawn the same as one that is full.
 *
 * A bucket with no colour -- the no-value bucket, and every field whose buckets have none --
 * gets no mark at all. A neutral one would read as a bucket whose colour happens to be grey.
 *
 * A bucket that is an account, such as an assignee, gets its avatar instead.
 * A bucket that is a repository gets its visibility icon.
 * A bucket that is an issue status gets the status icon the issue page draws.
 * The mark and its accessibility treatment stay together: repository state is meaningful,
 * while avatars, status icons and colours are decorative because the text names the bucket.
 */
export function axisLeadingVisual(label: BoardPayloadAxisLabel | undefined): AxisLeadingVisual {
  if (label?.repositoryStatus !== undefined) {
    return {
      leadingVisual: <RepositoryIcon status={label.repositoryStatus} />,
      leadingVisualAriaHidden: false,
    }
  }

  const issueStatus = label?.issueStatus
  if (issueStatus !== undefined) {
    return {leadingVisual: <IssueStatusIcon status={toIssueStatusIconStatus(issueStatus)} />}
  }

  // Slightly larger than the axis icons while staying compact in the heading.
  if (label?.avatarUrl !== undefined) {
    return {
      leadingVisual: <GitHubAvatar src={label.avatarUrl} size={18} alt="" />,
    }
  }

  const color = label?.color
  return {
    leadingVisual:
      color === undefined ? undefined : color.type === 'named' ? (
        <ColorDecorator color={NAMED_COLORS[color.name]} />
      ) : (
        <span data-testid="axis-color-swatch" className={styles.swatch} style={{backgroundColor: `#${color.hex}`}} />
      ),
  }
}

/**
 * What the heading reads, or nothing where the plain name the board already holds says it.
 *
 * `textHtml` is where a bucket's emoji lives -- a label called `:bug: bug` reads as its
 * shortcode without it -- and the payload types it as `SafeHTMLString`, which is what makes it
 * safe to render as markup. The bucket name itself is user-controlled and is never rendered as
 * one.
 */
export function axisHeadingText(label: BoardPayloadAxisLabel | undefined): ReactNode {
  const html = label?.textHtml
  if (html === undefined) return undefined

  return <VerifiedHTMLText as="span" html={html} />
}
